// LES CARTES HORS LIGNE (25/07, demande Claude-Alain : « je veux toutes les maps, et pouvoir en
// créer »). Deux mécanismes, volontairement dans le même module parce qu'ils répondent à la même
// question — « qu'est-ce que j'ai sur moi quand il n'y a pas de réseau ? » :
//
//   1. LE MIROIR de la bibliothèque famille. Chaque fois qu'on la charge avec succès, on en garde
//      une copie. Sans réseau, on joue cette copie. Elle est donc au plus vieille de ta dernière
//      connexion. ⚠️ On ne passe PAS par le cache du service worker : la règle « rien de /api/ en
//      cache » reste absolue (une réponse d'API resservie à l'aveugle casse le journal et les
//      soldes). Ici c'est l'APPLICATION qui garde une copie, délibérément, d'une seule route.
//
//   2. LA FILE DES BROUILLONS. Une carte créée sans réseau est rangée sur l'appareil avec un
//      identifiant LOCAL, jouable et modifiable tout de suite, et publiée au retour du réseau.
//      Elle prend alors son vrai numéro et rejoint la bibliothèque comme n'importe quelle carte.
//
// Toute la logique est PURE (le stockage est injecté) : testable sans navigateur.
import type { LevelFull } from '../shared/api-types'
import type { Storageish } from './score-queue'
import type { MonkSpeed } from '../shared/types'
import type { WorldKey } from '../shared/worlds'

/** Clé du miroir de la bibliothèque. */
export const LIB_KEY = 'mad.lib'
/** Clé de la file des brouillons à publier. */
export const DRAFTS_KEY = 'mad.drafts'
/** Plafond du miroir : une famille n'ira jamais là, mais un stockage sans borne est un bug qui dort. */
export const LIB_MAX = 150
/** Plafond des brouillons en attente. */
export const DRAFTS_MAX = 30

/** Lecture JSON tolérante : un contenu illisible rend [] plutôt que de faire échouer le démarrage. */
function readList<T>(store: Storageish, key: string, ok: (v: unknown) => v is T): T[] {
  try {
    const raw = store.getItem(key)
    if (!raw) return []
    const parsed: unknown = JSON.parse(raw)
    return Array.isArray(parsed) ? parsed.filter(ok) : []
  } catch {
    return []
  }
}

/** Écriture silencieuse : quota dépassé ou stockage interdit ne doit jamais remonter au jeu. */
function writeList(store: Storageish, key: string, v: unknown): void {
  try {
    store.setItem(key, JSON.stringify(v))
  } catch {
    /* navigation privée / quota : on renonce sans bruit */
  }
}

// --- 1. Le miroir de la bibliothèque -----------------------------------------------------------

/** Une entrée de miroir minimalement crédible : de quoi AFFICHER et JOUER la carte. */
function isMirroredLevel(v: unknown): v is LevelFull {
  const l = v as LevelFull
  return typeof v === 'object' && v !== null &&
    typeof l.id === 'number' && typeof l.name === 'string' && typeof l.ascii === 'string' && l.ascii.length > 0
}

/** Range la bibliothèque telle qu'elle vient d'être reçue. Appelé à CHAQUE chargement réussi. */
export function saveLibrary(store: Storageish, levels: readonly LevelFull[]): void {
  writeList(store, LIB_KEY, levels.slice(0, LIB_MAX))
}

/** La bibliothèque telle qu'on l'avait à la dernière connexion ([] si on n'a jamais chargé). */
export function readLibrary(store: Storageish): LevelFull[] {
  return readList(store, LIB_KEY, isMirroredLevel)
}

// --- 2. La file des brouillons -----------------------------------------------------------------

/** Une carte créée sans réseau, en attente de publication. */
export interface LocalDraft {
  localId: string
  name: string
  ascii: string
  world: WorldKey
  monkSpeed: MonkSpeed
  smartMonks: boolean
  monkPowers: boolean
  decor: string
  at: number
}

function isDraft(v: unknown): v is LocalDraft {
  const d = v as LocalDraft
  return typeof v === 'object' && v !== null &&
    typeof d.localId === 'string' && typeof d.name === 'string' &&
    typeof d.ascii === 'string' && d.ascii.length > 0
}

/** Les brouillons en attente, du plus récent au plus ancien. */
export function readDrafts(store: Storageish): LocalDraft[] {
  return readList(store, DRAFTS_KEY, isDraft).sort((a, b) => b.at - a.at)
}

/**
 * Range un brouillon. Si `localId` existe déjà, on REMPLACE : modifier une carte pas encore
 * publiée ne doit jamais en créer une seconde. C'est ce qui rend l'aller-retour éditeur sans
 * réseau naturel — on enregistre autant de fois qu'on veut, il n'en partira qu'une.
 */
export function saveDraft(store: Storageish, draft: LocalDraft): LocalDraft[] {
  const all = readList(store, DRAFTS_KEY, isDraft)
  const i = all.findIndex((d) => d.localId === draft.localId)
  if (i >= 0) all[i] = draft
  else all.push(draft)
  // Débordement : on sacrifie les plus ANCIENS (le travail récent est celui qui compte).
  const kept = all.length > DRAFTS_MAX ? [...all].sort((a, b) => a.at - b.at).slice(all.length - DRAFTS_MAX) : all
  writeList(store, DRAFTS_KEY, kept)
  return kept
}

/** Retire un brouillon (publié, supprimé par son auteur, ou refusé sans espoir). */
export function dropDraft(store: Storageish, localId: string): LocalDraft[] {
  const kept = readList(store, DRAFTS_KEY, isDraft).filter((d) => d.localId !== localId)
  writeList(store, DRAFTS_KEY, kept)
  return kept
}

/**
 * Un identifiant local sans ambiguïté. Préfixe `loc-` : impossible de le confondre avec un id serveur
 * (un entier), et lisible dans le stockage quand on débogue. `n` évite la collision de deux
 * créations dans la même milliseconde.
 */
let compteur = 0
export function newLocalId(now: number): string {
  compteur += 1
  return `loc-${now}-${compteur}`
}
